import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Calendar, Users } from 'lucide-react';
import { useBranding } from '@/context/BrandingContext';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { branding, getLogoUrl } = useBranding();
  const navigate = useNavigate();
  const logoUrl = getLogoUrl();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(username, password);
      toast.success('Login realizado com sucesso!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Credenciais invalidas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center p-8 md:p-12">
        <div className="w-full max-w-md space-y-8">
          <div className="space-y-3">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center overflow-hidden">
                {logoUrl ? (
                  <img src={logoUrl} alt={branding.system_name} className="w-full h-full object-contain bg-white" />
                ) : (
                  <Calendar className="w-6 h-6 text-primary-foreground" />
                )}
              </div>
              <h1 className="text-2xl font-manrope font-bold tracking-tight">{branding.system_name}</h1>
            </div>
            <h2 className="text-4xl font-manrope font-bold tracking-tight">
              Acesso ao Sistema
            </h2>
            <p className="text-muted-foreground font-inter">
              Entre com seu usuario para acessar o sistema restrito.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-medium">
                Usuario
              </Label>
              <Input
                id="username"
                data-testid="login-username-input"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Digite seu usuario"
                required
                className="h-12 bg-white/50 backdrop-blur-sm border-border/50 focus:border-primary"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">
                Senha
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="********"
                required
                className="h-12 bg-white/50 backdrop-blur-sm border-border/50 focus:border-primary"
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1765371512971-9d4da531d004?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxjcmVhdGl2ZSUyMG9mZmljZSUyMG1lZXRpbmclMjBzcGFjZSUyMHBsYW50c3xlbnwwfHx8fDE3Njg5NjY2MDl8MA&ixlib=rb-4.1.0&q=85"
            alt="Modern office meeting space"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent" />
        </div>
        <div className="absolute bottom-12 left-12 right-12 text-white z-10">
          <h3 className="text-3xl font-manrope font-bold tracking-tight mb-4">
            Gerencie suas salas de reuniao com eficiencia
          </h3>
          <div className="flex items-center gap-2 text-white/90">
            <Users className="w-5 h-5" />
            <span className="font-inter">Sistema colaborativo para toda a equipe</span>
          </div>
        </div>
      </div>
    </div>
  );
}
